import { useCallback, useContext, useState } from "react";

import { Page } from "@/components/ui/Layout/Page";
import { ChatInput } from "@/components/ChatInput";
import Loading from "@/components/ui/Feedback/Loading";
import ChatbotContext from "@/components/Chatbot/contexts/ChatbotContext";
import { useChatMessages } from "@/components/Chatbot/hooks/useChatMessages";
import { useMessageHandlers } from "@/components/Chatbot/hooks/useMessageHandlers";

import { useStyles } from "./styles";
import { EmptyState } from "./components/EmptyState";
import { MessageContent } from "./components/MessageContent";
import { useDocumentStatus } from "./hooks/useDocumentStatus";
import { useDocumentUpload } from "./hooks/useDocumentUpload";
import { useDocumentSelection } from "./hooks/useDocumentSelection";
import { ProcessingIndicator } from "./components/ProcessingIndicator";

export function ThreadPage() {
  const { setThreadId, setThreadTitle, threadId } = useContext(ChatbotContext);
  const styles = useStyles();

  const { handleTypingProgress } = useMessageHandlers();
  const [message, setMessage] = useState("");

  const { documentVersionId, retryUpload, uploadError } = useDocumentUpload();
  const { contextText, selectedText } = useDocumentSelection();
  const { documentStatus, isDocumentReady } = useDocumentStatus(documentVersionId);

  const onThreadUpdate = useCallback(
    (thread: { title?: string; uuid: null | number | string }) => {
      setThreadId(thread.uuid);
      if (setThreadTitle && thread.title) {
        setThreadTitle(thread.title);
      }
    },
    [setThreadId, setThreadTitle]
  );

  const {
    handleDismissMessage,
    handleRetryMessage,
    handleTypingComplete,
    isLoadingMessages,
    isLoadingSend,
    messages,
    sendMessage,
  } = useChatMessages({
    documentVersionId,
    onThreadUpdate,
    selectedText,
    threadId,
  });

  const handleSubmit = useCallback(
    (value: string) => {
      if (!value.trim()) return;
      setMessage("");
      sendMessage(value);
    },
    [sendMessage]
  );

  const handleActionClick = useCallback(
    (actionText: string) => {
      sendMessage(actionText);
    },
    [sendMessage]
  );

  const hasMessages = messages.length > 0;
  const isLoading = isLoadingMessages || isLoadingSend;

  return (
    <Page>
      <div className={styles.containerWrapper}>
        <div className={styles.container}>
          {hasMessages ? (
            <MessageContent
              messages={messages}
              onDismiss={handleDismissMessage}
              onRetry={handleRetryMessage}
              onTypingComplete={handleTypingComplete}
              onTypingProgress={handleTypingProgress}
              styles={styles}
              threadId={threadId}
            />
          ) : (
            <EmptyState onActionClick={handleActionClick} styles={styles} />
          )}

          {/* {isLoading && (
            <div className={styles.loadingContainer}>
              <Loading />
            </div>
          )} */}
        </div>

        <div className={`${styles.inputSection} ${hasMessages ? styles.inputSectionWithBorder : ""}`}>
          <ProcessingIndicator
            documentStatus={documentStatus}
            onRetry={retryUpload}
            styles={styles}
            uploadError={uploadError}
          />
          <ChatInput
            contextText={contextText}
            disableSubmit={!isDocumentReady}
            documentStatus={documentStatus}
            onSubmit={handleSubmit}
            placeholder="Ask anything about the document"
            showContextIndicator={true}
            value={message}
          />
        </div>
      </div>
    </Page>
  );
}
